// /accounts/$accountSlug — read-only account header + nested tenant
// list with a "Create tenant" CTA that opens TenantCreateForm in a
// modal.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { accounts } from "../../api/endpoints/accounts";
import { tenants } from "../../api/endpoints/tenants";
import { adminQueryOptions } from "../../api/adminQueryOptions";
import { qk } from "../../api/queryKeys";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorBanner } from "../../components/ui/ErrorBanner";
import { Modal } from "../../components/ui/Modal";
import { Table } from "../../components/ui/Table";
import { PageHeader } from "../../components/shell/PageHeader";
import { Pill } from "../../components/ui/Pill";
import type { TenantOut } from "../../api/types/tenant";

import { TenantCreateForm } from "../tenants/TenantCreateForm";
import { VaultSyncPill } from "../tenants/VaultSyncPill";

interface AccountDetailPageProps {
  accountSlug: string;
}

export function AccountDetailPage({ accountSlug }: AccountDetailPageProps) {
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const accountQuery = useQuery({
    ...adminQueryOptions,
    queryKey: qk.account(accountSlug),
    queryFn: () => accounts.get(accountSlug).then((r) => r.data),
  });

  const tenantsQuery = useQuery({
    ...adminQueryOptions,
    queryKey: qk.tenants(accountSlug),
    queryFn: () => tenants.list(accountSlug).then((r) => r.data),
  });

  return (
    <>
      <PageHeader
        title={accountQuery.data?.display_name ?? accountSlug}
        subtitle={accountSlug}
        actions={
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            style={primaryBtn}
          >
            Create tenant
          </button>
        }
      />

      {accountQuery.error && (
        <ErrorBanner error={accountQuery.error} title="Failed to load account" />
      )}

      {accountQuery.data && (
        <section style={card}>
          <dl style={dl}>
            <dt>Kind</dt>
            <dd>
              <Pill tone="info">{accountQuery.data.account_kind}</Pill>
            </dd>
            <dt>Status</dt>
            <dd>{accountQuery.data.status}</dd>
            <dt>Created</dt>
            <dd>{new Date(accountQuery.data.created_at).toLocaleString()}</dd>
            <dt>Activated</dt>
            <dd>
              {accountQuery.data.activated_at
                ? new Date(accountQuery.data.activated_at).toLocaleString()
                : "—"}
            </dd>
          </dl>
        </section>
      )}

      <section style={{ marginTop: 24 }}>
        <h3 style={sectionHeading}>Tenants</h3>
        {tenantsQuery.isPending && (
          <p style={{ color: "var(--text-dim)" }}>Loading tenants…</p>
        )}
        {tenantsQuery.error && (
          <ErrorBanner
            error={tenantsQuery.error}
            title="Failed to list tenants"
          />
        )}
        {tenantsQuery.data && (
          <Table<TenantOut>
            columns={[
              {
                key: "slug",
                header: "Slug",
                render: (t) => (
                  <Link
                    to="/accounts/$accountSlug/tenants/$tenantSlug"
                    params={{ accountSlug, tenantSlug: t.slug }}
                    style={{ color: "var(--accent)" }}
                  >
                    {t.slug}
                  </Link>
                ),
              },
              { key: "display_name", header: "Display name" },
              {
                key: "vault_sync_status",
                header: "Vault sync",
                render: (t) => (
                  <VaultSyncPill
                    status={t.vault_sync_status}
                    title={t.vault_sync_last_error ?? undefined}
                  />
                ),
              },
              { key: "status", header: "Status" },
            ]}
            rows={tenantsQuery.data}
            rowKey={(t) => t.id}
            empty={
              <EmptyState
                title="No tenants in this account yet"
                description="Click 'Create tenant' to add one — POST writes both Postgres and Vault."
              />
            }
          />
        )}
      </section>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        closeDisabled={creating}
        title={`Create tenant in ${accountSlug}`}
        width={520}
      >
        <TenantCreateForm
          accountSlug={accountSlug}
          onDone={() => setCreateOpen(false)}
          onPendingChange={setCreating}
        />
      </Modal>
    </>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)",
  color: "var(--bg)",
  border: "none",
  padding: "8px 16px",
  borderRadius: 4,
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
};

const card: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: 6,
  padding: 16,
};

const dl: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  gap: "6px 16px",
  margin: 0,
  fontSize: "0.9rem",
};

const sectionHeading: React.CSSProperties = {
  margin: "0 0 12px",
  fontSize: "0.95rem",
  color: "var(--text-dim)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};
